import React from 'react';
import PropTypes from 'prop-types';
import { LuPackage, LuPackageCheck, LuUserPlus } from 'react-icons/lu';
import Modal from '../../../components/ui/Modal';
import Button from '../../../components/ui/Button';
import GlassIcon from '../../../components/ui/GlassIcon';
import { Field, SelectInput, TextArea, TextInput } from '../../../components/form/FormControls';
import useStockText from '../stockText';

const Figure = ({ icon: Icon, label, value, tone }) => (
  <div className="tw-flex tw-items-center tw-gap-3 tw-rounded-2xl tw-bg-slate-500/[0.06] tw-p-3 dark:tw-bg-white/[0.04]">
    <span className={`tw-grid tw-h-9 tw-w-9 tw-shrink-0 tw-place-items-center tw-rounded-xl ${tone}`}>
      <Icon aria-hidden="true" className="tw-h-4 tw-w-4" />
    </span>
    <span className="tw-min-w-0">
      <span className="tw-block tw-text-[11px] tw-font-medium tw-text-slate-500 dark:tw-text-slate-400">{label}</span>
      <span className="tw-block tw-text-lg tw-font-bold tw-tabular-nums tw-text-slate-900 dark:tw-text-white">{value}</span>
    </span>
  </div>
);

Figure.propTypes = { icon: PropTypes.elementType.isRequired, label: PropTypes.string.isRequired, value: PropTypes.node, tone: PropTypes.string.isRequired };

/**
 * "Stok Ata": gives part of the remaining quantity to an active employee
 * (a StockMovements record of type assignment; the remaining drops).
 */
const StockAssignModal = ({ isOpen, stock, employees, data, setData, onSave, onClose }) => {
  const { t, language, label } = useStockText();
  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="md"
      title={label('assign.title')}
      subtitle={stock?.['Adı']}
      closeLabel={t('common.close')}
      lang={language}
      icon={(
        <GlassIcon gradient="tw-from-teal-400 tw-to-cyan-600 tw-shadow-teal-500/30" size="xs">
          <LuUserPlus aria-hidden="true" className="tw-h-[18px] tw-w-[18px]" />
        </GlassIcon>
      )}
      footer={(
        <>
          <Button variant="ghost" onClick={onClose}>{label('common.cancel')}</Button>
          <Button variant="primary" onClick={onSave}>
            <LuUserPlus aria-hidden="true" className="tw-h-4 tw-w-4" />
            {label('assign.submit')}
          </Button>
        </>
      )}
    >
      <div className="tw-space-y-5">
        <div className="tw-grid tw-grid-cols-2 tw-gap-3">
          <Figure icon={LuPackage} label={label('assign.total')} value={stock?.['Miktar'] || 0} tone="tw-bg-slate-500/10 tw-text-slate-600 dark:tw-text-slate-300" />
          <Figure icon={LuPackageCheck} label={label('assign.remaining')} value={stock?.['Kalan'] || 0} tone="tw-bg-emerald-500/10 tw-text-emerald-600 dark:tw-text-emerald-300" />
        </div>
        <Field label={label('assign.employee')} htmlFor="assignEmployee" required>
          <SelectInput
            id="assignEmployee"
            value={data.employeeId}
            onChange={(e) => setData({ ...data, employeeId: e.target.value })}
            placeholder={label('assign.employeePlaceholder')}
            options={employees.map((emp) => ({ value: emp.id, label: `${emp.eName} ${emp.eSurname || ''}`.trim() }))}
          />
        </Field>
        <Field label={label('assign.quantity')} htmlFor="assignQuantity" required hint={label('assign.max', { max: stock?.['Kalan'] || 0 })}>
          <TextInput
            id="assignQuantity"
            type="number"
            min="0"
            step="1"
            placeholder={label('assign.quantityPlaceholder')}
            value={data.quantity}
            onChange={(e) => setData({ ...data, quantity: e.target.value })}
            className="tw-tabular-nums"
          />
        </Field>
        <Field label={label('assign.note')} htmlFor="assignNote">
          <TextArea
            id="assignNote"
            rows={3}
            placeholder={label('assign.notePlaceholder')}
            value={data.note}
            onChange={(e) => setData({ ...data, note: e.target.value })}
          />
        </Field>
      </div>
    </Modal>
  );
};

StockAssignModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  stock: PropTypes.object,
  employees: PropTypes.array.isRequired,
  data: PropTypes.shape({ employeeId: PropTypes.string, quantity: PropTypes.oneOfType([PropTypes.string, PropTypes.number]), note: PropTypes.string }).isRequired,
  setData: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default StockAssignModal;
